import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";
import { Calculator } from "@/components/views/calculator/calculator";
import { FAQ_SECTIONS } from "@/components/views/faq/faq-data";
import type { Ingredient, Recipe } from "@/lib/types";
import {
  db,
  ingredientColumns,
  ingredientColumnSelection,
  ingredients,
  recipes,
} from "@/db/index.server";
import { and, eq, inArray, or } from "drizzle-orm";
import { auth } from "@/lib/auth";
import { optionalStringSearchParam } from "@/lib/searchParams";
import { queryTargetProfiles } from "@/lib/target-profiles/queries.server";
import { queryMachines } from "@/lib/machines/queries.server";
import { queryServingPresets } from "@/lib/serving-presets/queries.server";
import { transformRecipe } from "@/lib/recipes/transformRecipe";
import type { WebApplication, WithContext } from "schema-dts";

const TITLE = "Ice Cream Calculator";
const DESCRIPTION =
  "Make your best ice cream with Scoopulator, an approachable ice cream calculator. Dial in the composition, sweetness, and scoopability with scientific precision.";
const CANONICAL_URL = "https://scoopulator.app/calc";
const IMAGE_URL = "https://scoopulator.app/screenshots/calculator.webp";

const CALCULATOR_FAQ_SECTION = FAQ_SECTIONS.find(
  (section) => section.id === "using-the-calculator",
);

const CALCULATOR_FAQ_STRUCTURED_DATA = CALCULATOR_FAQ_SECTION
  ? {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: CALCULATOR_FAQ_SECTION.items.map((item) => ({
        "@type": "Question" as const,
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer" as const,
          text: item.answer,
        },
      })),
    }
  : null;

const CALCULATOR_APP_STRUCTURED_DATA: WithContext<WebApplication> = {
  "@context": "https://schema.org",
  "@type": "WebApplication",
  name: `${TITLE} - Scoopulator`,
  description: DESCRIPTION,
  url: CANONICAL_URL,
  image: IMAGE_URL,
  applicationCategory: "UtilitiesApplication",
  operatingSystem: "Web",
  browserRequirements: "Requires JavaScript. Works in modern web browsers.",
  isAccessibleForFree: true,
  featureList: ["Ice cream calculator", "Ninja Creami calculator"],
  offers: {
    "@type": "Offer",
    price: "0.00",
    priceCurrency: "USD",
  },
};

const searchSchema = z.object({
  ingredients: optionalStringSearchParam,
  recipe: optionalStringSearchParam,
  evp: z.number().optional().catch(undefined),
  type: optionalStringSearchParam,
  target: optionalStringSearchParam,
  machine: optionalStringSearchParam,
  firmness: optionalStringSearchParam,
  serving: optionalStringSearchParam,
});

function collectPrefetchIngredientSlugs(searchIngredients: string | undefined) {
  const searchParamsSlugs = searchIngredients
    ? searchIngredients.split(",").map((str: string) => str.split(":")[0])
    : [];
  const allSlugs = new Set(searchParamsSlugs);
  return Array.from(allSlugs);
}

function queryIngredients(slugs: string[]): Promise<Ingredient[]> {
  if (slugs.length === 0) return Promise.resolve([]);
  return db
    .select(ingredientColumns)
    .from(ingredients)
    .where(inArray(ingredients.slug, slugs));
}

async function querySelectedRecipe(
  recipeSlug: string,
  userId: string | null,
): Promise<Recipe | null> {
  const recipe = await db._query.recipes.findFirst({
    where: and(
      eq(recipes.slug, recipeSlug),
      or(
        eq(recipes.isPublic, true),
        userId ? eq(recipes.createdBy, userId) : undefined,
      ),
    ),
    with: {
      recipeIngredients: {
        with: {
          ingredient: {
            columns: ingredientColumnSelection,
          },
        },
      },
      user: true,
      machine: true,
    },
  });

  if (!recipe) return null;
  return transformRecipe(recipe);
}

const getCalcData = createServerFn({ method: "GET" })
  .inputValidator((data: { ingredients?: string; recipe?: string }) => data)
  .handler(async ({ data }) => {
    const request = getRequest();
    const session = await auth.api.getSession({ headers: request.headers });
    const userId = session?.user?.id ?? null;

    const recipeSlug = data.recipe?.trim() || null;
    const slugs = collectPrefetchIngredientSlugs(data.ingredients);

    const [
      prefetchedIngredients,
      targetProfiles,
      machines,
      servingPresets,
      selectedRecipe,
    ] = await Promise.all([
      queryIngredients(slugs),
      queryTargetProfiles(userId),
      queryMachines(userId),
      queryServingPresets(userId),
      recipeSlug
        ? querySelectedRecipe(recipeSlug, userId)
        : Promise.resolve(null),
    ]);

    return {
      currentUserId: userId,
      isSignedIn: userId !== null,
      ingredients: prefetchedIngredients,
      targetProfiles,
      machines,
      servingPresets,
      selectedRecipe,
    };
  });

export const Route = createFileRoute("/calc")({
  validateSearch: searchSchema,
  head: () => ({
    meta: [
      { title: `${TITLE} - Scoopulator` },
      { name: "description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:title", content: `${TITLE} - Scoopulator` },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: CANONICAL_URL },
      { property: "og:image", content: IMAGE_URL },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(CALCULATOR_APP_STRUCTURED_DATA).replace(
          /</g,
          "\\u003c",
        ),
      },
      ...(CALCULATOR_FAQ_STRUCTURED_DATA
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify(CALCULATOR_FAQ_STRUCTURED_DATA).replace(
                /</g,
                "\\u003c",
              ),
            },
          ]
        : []),
    ],
  }),
  shouldReload: ({ cause }) => cause !== "stay",
  loader: async ({ location }) => {
    const { ingredients, recipe } = searchSchema.parse(location.search);
    const data = await getCalcData({
      data: { ingredients, recipe },
    });
    return data;
  },
  component: CalcPage,
});

function CalcPage() {
  const data = Route.useLoaderData();

  return (
    <Calculator
      currentUserId={data.currentUserId}
      isSignedIn={data.isSignedIn}
      ingredients={data.ingredients}
      selectedRecipe={data.selectedRecipe}
      targetProfiles={data.targetProfiles}
      machines={data.machines}
      servingPresets={data.servingPresets}
    />
  );
}
